import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { IUserInfo } from '../models/identity/user-info';
import { setInitialState, setIsTokenExpired } from '../../redux/features/auth';
import { IPointOfSale } from '../models/core/point-of-sale';
import { ISalesOffice } from '../models/core/sales-office';
import { authApi, useGetSessionUserQuery, useLogoutUserMutation } from '../../redux/api/auth';
import { setLogoutNotice } from '../../utility/auth/sessionSync';
import {
  isAuthRedirectInProgress,
  isPublicAuthRoute,
  redirectToLoginAfterUnauthorized,
} from '../../utility/auth/authRedirect';
import { apiUrl } from '../../environments/environment';
import {
  clearOfflineSessionUser,
  getOfflineSessionUser,
  saveOfflineSessionUser,
} from '../../offline/sessionCache';

interface QueryErrorWithStatus {
  status?: number | string;
  originalStatus?: number;
}

const getQueryHttpStatus = (error: unknown): number | string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const queryError = error as QueryErrorWithStatus;
  if (typeof queryError.status === 'number') {
    return queryError.status;
  }

  return queryError.originalStatus ?? queryError.status;
};

interface IAuthHook {
  isDealer: boolean;
  currentUser: IUserInfo | null;
  selectedPos: IPointOfSale | null;
  selectedSalesOffice: ISalesOffice | null;
  isTokenExpired: boolean | undefined;
  isDarkStore: boolean;
  isChangedPos: boolean;
  isAuthenticate: boolean;
  onLogout: (options?: { localOnly?: boolean; reason?: 'inactivity' }) => Promise<void>;
  getAuthUrl: (url: string) => string;
  permissionMatch: (...permission: string[]) => boolean | undefined;
}

export const useAuth = (): IAuthHook => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedPos, selectedSalesOffice, isTokenExpired, oldSelectedPos } = useAppSelector(
    (state) => state.auth
  );
  const shouldSkipSessionQuery = isPublicAuthRoute() || isAuthRedirectInProgress();
  const { data: sessionUser, error: sessionError } = useGetSessionUserQuery(undefined, {
    skip: shouldSkipSessionQuery,
  });
  const [onLogoutUser] = useLogoutUserMutation();

  useEffect(() => {
    if (sessionUser) saveOfflineSessionUser(sessionUser);
  }, [sessionUser]);

  const currentUser = useMemo(() => {
    if (sessionUser) return sessionUser;
    if (selectedPos?.isOffline && !selectedPos.isDealer) return getOfflineSessionUser();
    return null;
  }, [selectedPos?.isDealer, selectedPos?.isOffline, sessionUser]);

  useEffect(() => {
    if (shouldSkipSessionQuery || getQueryHttpStatus(sessionError) !== 401) {
      return;
    }

    dispatch(setInitialState());
    dispatch(setIsTokenExpired({ expired: true }));
    dispatch(authApi.util.resetApiState());
    redirectToLoginAfterUnauthorized('session_query_unauthorized');
  }, [dispatch, sessionError, shouldSkipSessionQuery]);

  const permissionMatch = (...permission: string[]) => {
    const currentPermisions = [];
    if (currentUser?.permissions && !Array.isArray(currentUser.permissions)) {
      currentPermisions.push(currentUser.permissions);
    } else if (currentUser?.permissions && Array.isArray(currentUser.permissions)) {
      currentPermisions.push(...currentUser.permissions);
    }

    return (
      (currentUser?.permissions && currentPermisions.some((r) => permission.includes(r))) ||
      currentUser?.system_admin
    );
  };

  const finalizeLogout = (reason?: 'inactivity') => {
    if (reason === 'inactivity') {
      setLogoutNotice('inactivity');
    }

    dispatch(authApi.util.resetApiState());
    clearOfflineSessionUser();
    dispatch(setInitialState());
    navigate('/login', { replace: true });
  };

  const onLogout = async (options?: { localOnly?: boolean; reason?: 'inactivity' }) => {
    if (options?.localOnly) {
      finalizeLogout(options.reason);
      return;
    }

    try {
      await onLogoutUser().unwrap();
    } catch (error) {
      console.warn('Logout request failed, clearing local session anyway.', error);
    }

    finalizeLogout(options?.reason);
  };

  const getAuthUrl = (url: string) => apiUrl(url);
  return {
    isDealer: selectedPos?.isDealer ?? false,
    currentUser: currentUser ?? null,
    selectedPos,
    selectedSalesOffice: selectedSalesOffice ?? null,
    isTokenExpired,
    isDarkStore: selectedPos?.isDarkStore ?? false,
    isChangedPos: !!(selectedPos && oldSelectedPos && selectedPos?.id !== oldSelectedPos?.id),
    isAuthenticate: !!(
      currentUser &&
      (currentUser.permissions?.length || currentUser.system_admin)
    ),
    onLogout,
    getAuthUrl,
    permissionMatch,
  };
};
